import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../hooks/use-toast";
import { formatDate, getPaymentStructureLabel, calculateVATInclusiveAmount } from "../../lib/appUtils";
import { ClientPaymentChoice } from "../shared/ClientPaymentChoice";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Toaster } from "../ui/toaster";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  CheckCircle, XCircle, Clock, ArrowRight, CreditCard, Shield, 
  Banknote, Mail, Zap, Eye, Signature, AlertTriangle, Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutPage = () => {
  const { slug } = useParams();
  console.log('CheckoutPage render - slug:', slug);
  
  const [projectData, setProjectData] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [contractSigned, setContractSigned] = useState(false);
  const [showFullContract, setShowFullContract] = useState(false);
  const [signatureData, setSignatureData] = useState({
    full_name: '',
    email: '',
    position: '',
    company: '',
    agreed: false,
    identity_confirmed: false
  });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [paymentElement, setPaymentElement] = useState(null);
  const [paymentIntentClientSecret, setPaymentIntentClientSecret] = useState(null);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakeApplePay, setCanMakeApplePay] = useState(false);
  const [canMakeGooglePay, setCanMakeGooglePay] = useState(false);
  const { toast } = useToast();
  
  // TEMPORARY DEBUG: Return simple test content
  if (!slug) {
    return <div>ERROR: No slug provided</div>;
  }

  useEffect(() => {
    fetchProjectData();
  }, [slug]);
  // Update signature data when project data becomes available
  useEffect(() => {
    if (projectData?.project) {
      setSignatureData(prev => ({
        ...prev,
        full_name: projectData.project.client_name || '',
        email: projectData.project.client_email || '',
        company: projectData.project.client_company || ''
      }));
    }
  }, [projectData]);

  const fetchProjectData = async () => {
    try {
      // Add cache-busting parameter to ensure fresh data after payment
      const timestamp = Date.now();
      console.log('fetchProjectData - API call:', `${API}/checkout/${slug}?t=${timestamp}`);
      const response = await axios.get(`${API}/checkout/${slug}?t=${timestamp}`);
      console.log('fetchProjectData - response:', response.data);
      console.log('fetchProjectData - contract_agreed:', response.data.project.contract_agreed);
      setProjectData(response.data);
      setUserSettings(response.data.user_settings);
      setContractSigned(response.data.project.contract_agreed);
      
      // Initialize Stripe with key from backend
      if (response.data.stripe_publishable_key && window.Stripe) {
        setStripe(window.Stripe(response.data.stripe_publishable_key));
      }
      
      // Auto-select first milestone if available
      if (response.data.project.milestones && response.data.project.milestones.length > 0) {
        setSelectedPaymentOption(response.data.project.milestones[0]);
      }
    } catch (error) {
      console.error('fetchProjectData - error:', error);
      if (error.response?.status === 410) {
        setProjectData({ 
          project: { status: 'archived' },
          error: 'This payment page is no longer active'
        });
      } else {
        setProjectData(null);
        console.error('Failed to load checkout:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending');

  // Generate device fingerprint for audit trail
  const generateDeviceFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvas: canvas.toDataURL(),
      timestamp: new Date().toISOString()
    };
  };

  const handleEnhancedSignContract = async () => {
    if (!signatureData.agreed || !signatureData.identity_confirmed || !signatureData.full_name?.trim() || !signatureData.email?.trim() || !signatureData.position?.trim()) {
      toast({
        title: "Required Information Missing",
        description: "Please complete all required fields and confirmations, including your position/title",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      // Generate enhanced audit trail
      const deviceFingerprint = generateDeviceFingerprint();
      const contractHash = btoa(JSON.stringify({
        project_id: projectData.project.id,
        signer_name: signatureData.full_name,
        signer_email: signatureData.email,
        timestamp: deviceFingerprint.timestamp
      }));

      const signaturePayload = {
        full_name: signatureData.full_name.trim(),
        email: signatureData.email.trim(),
        position: signatureData.position?.trim() || '',
        company: signatureData.company?.trim() || '',
        agreed: signatureData.agreed,
        identity_confirmed: signatureData.identity_confirmed,
        device_fingerprint: deviceFingerprint,
        contract_hash: contractHash,
        ip_address: '127.0.0.1', // Will be replaced by server with real IP
        user_agent: navigator.userAgent,
        signature_method: 'enhanced_electronic_uk_compliant'
      };

      await axios.post(`${API}/checkout/${slug}/sign-enhanced`, signaturePayload);

      setVerificationSent(true);
      toast({
        title: "Signature Captured Successfully",
        description: `Email verification has been sent to ${signatureData.email}. Please check your inbox and click the verification link to complete the signing process.`
      });

      // Poll for verification completion
      const checkVerification = setInterval(async () => {
        try {
          const response = await fetchProjectData();
          if (response?.data?.project?.contract_agreed === true) {
            clearInterval(checkVerification);
            setVerificationStatus('verified');
            toast({
              title: "Contract Fully Executed",
              description: "Your signature has been verified! The contract is now legally binding."
            });
          }
        } catch (error) {
          // Continue polling
        }
      }, 5000);

      // Stop polling after 10 minutes
      setTimeout(() => clearInterval(checkVerification), 600000);

    } catch (error) {
      toast({
        title: "Signature Failed",
        description: error.response?.data?.detail || "Failed to process signature",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleSignContract = async (e) => {
    e.preventDefault();
    if (!signatureData.agreed || !signatureData.full_name.trim()) {
      toast({
        title: "Agreement Required",
        description: "Please enter your full name and agree to the contract terms",
        variant: "destructive"
      });
      console.log('🔥 VALIDATION FAILED - returning early');
      return;
    }

    console.log('🔥 VALIDATION PASSED - proceeding with API call');
    setProcessing(true);
    try {
      await axios.post(`${API}/checkout/${slug}/sign`, {
        full_name: signatureData.full_name.trim(),
        agreed: signatureData.agreed,
        ip_address: '127.0.0.1',
        user_agent: navigator.userAgent
      });

      setContractSigned(true);
      toast({
        title: "Contract Signed",
        description: "Thank you! Redirecting to payment..."
      });
      
      // Refresh project data and ensure payment interface loads
      await fetchProjectData();
      
      // Force reload to show updated status (active project with milestones)
      window.location.reload();
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign contract",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentMethodSelect = async (paymentMethod) => {
    console.log('Payment method selected:', paymentMethod);
    
    // If switching payment methods, destroy existing elements and reset PaymentIntent
    if (elements && paymentElement) {
      console.log('Destroying existing payment element...');
      paymentElement.destroy();
      setPaymentElement(null);
      setElements(null);
    }
    
    // Reset PaymentIntent and PaymentRequest when switching methods
    setPaymentIntentClientSecret(null);
    setPaymentRequest(null);
    
    setSelectedPaymentMethod(paymentMethod);
    setShowPaymentMethods(true);
    
    // Always create a NEW PaymentIntent for the specific payment method
    if (selectedPaymentOption) {
      console.log('Creating payment intent for:', paymentMethod);
      try {
        // Map frontend payment method to Stripe payment_method_types
        let paymentMethodTypes = ['card'];
        if (paymentMethod === 'apple_pay') {
          paymentMethodTypes = ['card']; // Apple Pay requires card as base
        } else if (paymentMethod === 'google_pay') {
          paymentMethodTypes = ['card']; // Google Pay requires card as base
        } else if (paymentMethod === 'card') {
          paymentMethodTypes = ['card'];
        } else if (paymentMethod === 'bank_debit') {
          paymentMethodTypes = ['bacs_debit'];
        } else if (paymentMethod === 'klarna') {
          paymentMethodTypes = ['klarna'];
        } else if (paymentMethod === 'clearpay') {
          paymentMethodTypes = ['clearpay'];
        } else if (paymentMethod === 'bnpl') {
          paymentMethodTypes = ['bnpl'];
        }
        
        const response = await axios.post(`${API}/checkout/${slug}/create-payment-intent`, {
          milestone_id: selectedPaymentOption.id,
          payment_method_type: paymentMethodTypes[0]
        });
        
        console.log('Payment intent created:', response.data);
        setPaymentIntentClientSecret(response.data.client_secret);
        
        // Convert pounds to pence for Stripe Payment Request
        const amountInPence = Math.round(response.data.amount * 100);
        
        // For Apple Pay and Google Pay, use Payment Request Button (native UI)
        if (paymentMethod === 'apple_pay') {
          setupWalletPayment(response.data.client_secret, amountInPence, 'apple_pay');
        } else if (paymentMethod === 'google_pay') {
          setupWalletPayment(response.data.client_secret, amountInPence, 'google_pay');
        } else {
          // For card, bank debit and BNPL (Klarna/Clearpay), use PaymentElement (handles redirects for BNPL)
          mountPaymentElement(response.data.client_secret, paymentMethod);
        }
      } catch (error) {
        console.error('Failed to create payment intent:', error);
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to initialize payment",
          variant: "destructive"
        });
        setShowPaymentMethods(false);
      }
    } else {
      console.log('No milestone selected');
    }
  };
  
  // Setup Wallet Payment (Apple Pay or Google Pay) using Stripe Payment Request API
  const setupWalletPayment = (clientSecret, amountInPence, walletType) => {
    if (!stripe) {
      console.error('Stripe not initialized');
      return;
    }
    
    const walletName = walletType === 'apple_pay' ? 'Apple Pay' : 'Google Pay';
    console.log(`Setting up ${walletName} Payment Request...`);
    
    // Create a PaymentRequest object
    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: {
        label: projectData?.project?.title || 'Payment',
        amount: amountInPence, // Amount in pence
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });
    
    // Check if wallet payment is available
    pr.canMakePayment().then((result) => {
      console.log('canMakePayment result:', result);
      
      const isApplePayAvailable = result && result.applePay;
      const isGooglePayAvailable = result && result.googlePay;
      
      if (walletType === 'apple_pay') {
        setCanMakeApplePay(isApplePayAvailable);
        if (!isApplePayAvailable) {
          console.log('Apple Pay not available on this device');
          toast({
            title: "Apple Pay Not Available",
            description: "Apple Pay is not available on this device or browser. Please use Safari on an Apple device with Apple Pay enabled.",
            variant: "destructive"
          });
          return;
        }
      } else if (walletType === 'google_pay') {
        setCanMakeGooglePay(isGooglePayAvailable);
        if (!isGooglePayAvailable) {
          console.log('Google Pay not available on this device');
          toast({
            title: "Google Pay Not Available",
            description: "Google Pay is not available on this device or browser. Please use Chrome with Google Pay enabled.",
            variant: "destructive"
          });
          return;
        }
      }
      
      // Wallet is available, set up the button
      setPaymentRequest(pr);
      
      // Mount the Payment Request Button
      setTimeout(() => {
        const prButton = stripe.elements().create('paymentRequestButton', {
          paymentRequest: pr,
          style: {
            paymentRequestButton: {
              type: 'default',
              theme: walletType === 'apple_pay' ? 'dark' : 'light',
              height: '48px'
            }
          }
        });
        
        const container = document.getElementById('payment-request-button');
        if (container) {
          container.innerHTML = '';
          prButton.mount('#payment-request-button');
        }
      }, 100);
    });
    
    // Handle the paymentmethod event when user confirms wallet payment
    pr.on('paymentmethod', async (ev) => {
      console.log(`${walletName} payment method received:`, ev.paymentMethod);
      setProcessing(true);
      
      try {
        // Confirm the PaymentIntent with the payment method from wallet
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );
        
        if (confirmError) {
          console.error('Error confirming payment:', confirmError);
          ev.complete('fail');
          throw new Error(confirmError.message);
        }
        
        // Check if the payment requires additional action
        if (paymentIntent.status === 'requires_action') {
          const { error } = await stripe.confirmCardPayment(clientSecret);
          if (error) {
            ev.complete('fail');
            throw new Error(error.message);
          }
        }
        
        ev.complete('success');
        
        // Confirm payment on backend
        if (paymentIntent.status === 'succeeded') {
          console.log(`${walletName} payment succeeded, confirming on backend...`);
          await axios.post(`${API}/checkout/${slug}/confirm-payment?payment_intent_id=${paymentIntent.id}&milestone_id=${selectedPaymentOption.id}`);
          
          setPaymentSuccess(true);
          toast({
            title: "Payment Successful",
            description: `Thank you! Your payment has been processed successfully via ${walletName}.`
          });
          fetchProjectData();
        }
      } catch (error) {
        console.error(`${walletName} payment failed:`, error);
        setPaymentCancelled(true);
        toast({
          title: "Payment Failed",
          description: error.message || `Failed to process ${walletName} payment`,
          variant: "destructive"
        });
      } finally {
        setProcessing(false);
      }
    });
  };
  
  const mountPaymentElement = (clientSecret, paymentMethod) => {
    if (!stripe) {
      console.error('Stripe not initialized');
      return;
    }
    
    console.log('Mounting payment element for:', paymentMethod);
    
    const elementsInstance = stripe.elements({
      clientSecret: clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#0570de',
          colorBackground: '#ffffff',
          colorText: '#30313d',
          colorDanger: '#df1b41',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px',
        }
      }
    });
    
    let paymentElementConfig = {
      layout: {
        type: 'accordion',
        defaultCollapsed: false,
        radios: false,
        spacedAccordionItems: false
      }
    };
    
    // Configure which payment methods to show - disable wallets for card/bank
    if (paymentMethod === 'card') {
      paymentElementConfig.wallets = {
        applePay: 'never',
        googlePay: 'never'
      };
    } else if (paymentMethod === 'bank_debit') {
      paymentElementConfig.wallets = {
        applePay: 'never',
        googlePay: 'never'
      };
    }
    
    const paymentElementInstance = elementsInstance.create('payment', paymentElementConfig);
    
    setElements(elementsInstance);
    
    // Mount to the container
    setTimeout(() => {
      const paymentContainer = document.getElementById('payment-element');
      if (paymentContainer) {
        // Clear container first
        paymentContainer.innerHTML = '';
        console.log('Mounting payment element...');
        paymentElementInstance.mount('#payment-element');
        setPaymentElement(paymentElementInstance);
        console.log('Payment Element mounted for:', paymentMethod);
      } else {
        console.error('Payment container not found');
      }
    }, 100);
  };

  const handlePayment = async () => {
    if (!selectedPaymentOption || !selectedPaymentMethod || !contractSigned) {
      toast({
        title: "Payment Error",
        description: "Please complete all required steps before proceeding",
        variant: "destructive"
      });
      return;
    }

    if (!paymentIntentClientSecret) {
      toast({
        title: "Payment Error",
        description: "Please wait for payment to initialize",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      console.log('Processing payment...', {
        selectedPaymentMethod,
        stripe: !!stripe,
        elements: !!elements,
        paymentIntentClientSecret: !!paymentIntentClientSecret
      });
      
      // Process payment with Stripe Payment Element (supports all payment methods)
      if (!stripe || !elements) {
        console.error('Stripe not initialized:', { stripe: !!stripe, elements: !!elements });
        throw new Error('Stripe not initialized. Please refresh the page and try again.');
      }

      console.log('Confirming payment with Payment Element...');
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/${slug}?payment=success`,
        },
        redirect: 'if_required'
      });

      if (error) {
        console.error('Stripe error:', error);
        throw new Error(error.message);
      }

      console.log('Payment intent status:', paymentIntent?.status);
      
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm payment on backend
        console.log('Confirming payment on backend...');
        await axios.post(`${API}/checkout/${slug}/confirm-payment?payment_intent_id=${paymentIntent.id}&milestone_id=${selectedPaymentOption.id}`);

        // Set up scheduled payments for 50/50 and monthly instalments
        const paymentStructure = projectData?.project?.payment_structure;
        if (paymentStructure === 'fifty_fifty' || paymentStructure === 'three_month_milestones') {
          try {
            // Get payment method ID from the payment intent
            const paymentMethodId = paymentIntent.payment_method;
            
            if (paymentMethodId) {
              console.log('Setting up scheduled payments for', paymentStructure);
              await axios.post(`${API}/checkout/${slug}/setup-scheduled-payments`, {
                payment_intent_id: paymentIntent.id,
                payment_method_id: paymentMethodId,
                client_email: projectData?.project?.client_email || '',
                client_name: projectData?.project?.client_name || ''
              });
              console.log('Scheduled payments set up successfully');
            }
          } catch (scheduleError) {
            console.error('Failed to set up scheduled payments:', scheduleError);
            // Don't fail the main payment - just log the error
          }
        }

        setPaymentSuccess(true);
        toast({
          title: "Payment Successful",
          description: "Thank you! Your payment has been processed successfully."
        });

        fetchProjectData();
      }
    } catch (error) {
      setPaymentCancelled(true);
      toast({
        title: "Payment Failed",
        description: error.message || error.response?.data?.detail || "Failed to process payment",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handlePayAllRemaining = async () => {
    if (!selectedPaymentMethod || !contractSigned) {
      toast({
        title: "Payment Error", 
        description: "Please complete contract signing and select a payment method",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      const unpaidMilestones = projectData.project.milestones.filter(m => m.status !== 'paid');
      
      // Process all unpaid milestones
      for (const milestone of unpaidMilestones) {
        await axios.post(`${API}/checkout/${slug}/pay/${milestone.id}`);
      }
      
      setPaymentSuccess(true);
      toast({
        title: "All Payments Successful",
        description: `Thank you! All ${unpaidMilestones.length} remaining payments have been processed.`
      });

      fetchProjectData();
    } catch (error) {
      setPaymentCancelled(true);
      toast({
        title: "Payment Failed",
        description: "Failed to process early payment. Please try individual instalments.",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatUKDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      timeZone: 'Europe/London'
    });
  };

  const getPaymentOptionLabel = (milestone, index, structure) => {
    if (structure === 'full_upfront') return 'Full Payment';
    if (structure === 'fifty_fifty') return index === 0 ? '1st Instalment (50%)' : '2nd Instalment (50%)';
    if (structure === 'three_month_milestones') {
      const labels = ['1st Instalment (40%)', '2nd Instalment (30%)', '3rd Instalment (30%)'];
      return labels[index] || `${index + 1}th Instalment`;
    }
    return milestone.title;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body">Loading secure checkout...</p>
        </div>
      </div>
    );
  }

  // Inactive state (archived project)
  if (!projectData || projectData.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Payment Page Inactive</h1>
            <p className="text-gray-600 font-body mb-4">
              {projectData?.error || "This payment page is no longer available."}
            </p>
            <p className="text-sm text-gray-500 font-body">
              Please contact the service provider for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { project, provider, contract } = projectData;

  // Show client choice interface if project is pending client choice OR if forced via URL parameter (but only if no choice has been made yet)
  const urlParams = new URLSearchParams(window.location.search);
  const showFullFlow = urlParams.get('flow') === 'full';
  
  // Show payment choice interface for pending projects OR when forced via ?flow=full parameter
  if (project.status === 'pending_client_choice' || showFullFlow) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-gray-900 font-heading">Paify</h1>
                <span className="text-gray-400">•</span>
                <span className="text-lg font-semibold text-gray-700 font-heading">{provider.business_name}</span>
              </div>
              <div className="flex items-center space-x-4">
                {/* Trust Badge */}
                {userSettings?.trust_badge_enabled && (
                  <div className="bg-gradient-to-r from-[#e6007e] to-pink-600 text-white px-3 py-1 rounded-full flex items-center space-x-2 text-xs font-medium shadow-lg">
                    <Shield className="w-3 h-3" />
                    <span>Secured by Paify</span>
                  </div>
                )}
                {/* Existing secure checkout indicator */}
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-600 font-body">Secure checkout</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Client Choice Interface */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <ClientPaymentChoice 
            project={project} 
            provider={provider}
            onChoiceComplete={() => {
              // Force reload without flow parameter to show checkout
              const url = new URL(window.location);
              url.searchParams.delete('flow');
              window.location.replace(url.toString());
            }}
            onRefreshData={fetchProjectData}
          />
        </main>

        <Toaster />
      </div>
    );
  }

  // Show pending review status
  if (project.status === 'pending_review') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <Clock className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Choice Under Review</h1>
            <p className="text-gray-600 font-body mb-4">
              Thank you for choosing your payment plan and signing the contract.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800 font-body">
                <strong>{provider.business_name}</strong> is reviewing your choice and will confirm shortly.
                You'll receive an email notification once approved.
              </p>
            </div>
            <p className="text-sm text-gray-500 font-body">
              Questions? Contact {provider.business_name} directly.
            </p>
          </div>
        </div>
      </div>
    );
  }
  // Fix VAT calculation to be ADDITIVE (not inclusive)
  const netAmount = project.total_amount; // Net amount is the project value
  const vatAmount = project.vat_enabled ? (netAmount * (project.vat_rate / 100)) : 0;
  const totalWithVat = netAmount + vatAmount; // VAT added on top

  // Success state
  if (paymentSuccess) {
    const { project, provider } = projectData;
    const paidAmount = selectedPaymentOption?.amount || 0;
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const formattedTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const refId = `PAY-${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}-${Math.random().toString(36).substr(2,6).toUpperCase()}`;
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-lg mx-auto px-6 py-12">
          {/* Success Animation */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6 animate-bounce">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Payment Confirmed</h1>
            <p className="text-lg text-gray-600 font-body">
              Your payment has been successfully processed
            </p>
          </div>

          {/* Payment Receipt Card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-6">
            <div className="bg-green-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <span className="text-white font-semibold font-heading">Payment Receipt</span>
                <span className="text-green-100 text-sm font-body">{formattedDate}</span>
              </div>
            </div>
            
            <div className="px-6 py-5 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500 font-body">Amount Paid</p>
                  <p className="text-2xl font-bold text-gray-900 font-heading">£{paidAmount.toLocaleString()}</p>
                </div>
                <div className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                  PAID
                </div>
              </div>
              
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-body">Project</span>
                  <span className="text-gray-900 font-medium font-body">{project.title}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-body">Service Provider</span>
                  <span className="text-gray-900 font-medium font-body">{provider.business_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-body">Reference</span>
                  <span className="text-gray-900 font-mono text-xs">{refId}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-body">Date & Time</span>
                  <span className="text-gray-900 font-body">{formattedDate} at {formattedTime}</span>
                </div>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4 font-heading">What happens next</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 font-body">Payment confirmed</p>
                  <p className="text-xs text-gray-500 font-body">A receipt has been sent to your email</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ArrowRight className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 font-body">Work begins now</p>
                  <p className="text-xs text-gray-500 font-body">{provider.business_name} has been notified and will start delivering your project</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 font-body">You're protected</p>
                  <p className="text-xs text-gray-500 font-body">Payments are processed by Stripe. Paify provides the contract verification and full audit trail.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-xs text-gray-400 font-body">
              Secured by Paify — Trusted payment infrastructure for service businesses
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Cancelled state
  if (paymentCancelled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Payment Cancelled</h1>
            <p className="text-gray-600 font-body mb-6">
              Your payment was not completed. You can try again or contact support if you continue to experience issues.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  setPaymentCancelled(false);
                  setSelectedPaymentMethod(null);
                }}
                className="w-full bg-black hover:bg-gray-800 text-white font-body"
              >
                Try Again
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.reload()}
                className="w-full font-body"
              >
                Start Over
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Verification Pending state - show waiting screen after signing
  if (verificationSent && !contractSigned) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <Mail className="w-16 h-16 text-amber-500" />
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Check Your Email</h1>
            <p className="text-gray-600 font-body mb-4">
              We've sent a verification email to:
            </p>
            <div className="bg-amber-50 rounded-lg p-4 mb-4">
              <p className="text-amber-800 font-semibold font-body">
                {projectData?.project?.contract_signer_email || signatureData.email}
              </p>
            </div>
            <p className="text-sm text-gray-500 font-body mb-6">
              Click the verification link in the email to complete signing the contract. This page will automatically update once verified.
            </p>
            <div className="flex items-center justify-center space-x-2 text-amber-600 mb-6">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent"></div>
              <span className="text-sm font-body">Waiting for verification...</span>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-400 font-body">
                Didn't receive the email? Check your spam folder or contact support.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900 font-heading">Paify</h1>
              <span className="text-gray-400">•</span>
              <span className="text-lg font-semibold text-gray-700 font-heading">{provider.business_name}</span>
            </div>
            <div className="flex items-center space-x-4">
              {/* Trust Badge */}
              {userSettings?.trust_badge_enabled && (
                <div className="bg-gradient-to-r from-[#e6007e] to-pink-600 text-white px-3 py-1 rounded-full flex items-center space-x-2 text-xs font-medium shadow-lg">
                  <Shield className="w-3 h-3" />
                  <span>Secured by Paify</span>
                </div>
              )}
              {/* Existing secure checkout indicator */}
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-green-600" />
                <span className="text-sm text-gray-600 font-body">Secure checkout</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - E-commerce Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12">
          {/* Order Summary - Left Side / Top on Mobile */}
          <div className="lg:col-span-7 mb-8 lg:mb-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:p-8 lg:sticky lg:top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 font-heading">Order Summary</h2>
              
              {/* Project Details */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">{project.title}</h3>
                <p className="text-gray-600 text-sm font-body mb-4">
                  {project.custom_scope || "Professional service delivery as agreed"}
                </p>
                <div className="flex items-center text-sm text-gray-500 font-body">
                  <span>Service provider: {provider.business_name}</span>
                  <span className="mx-2">•</span>
                  <span>Created {formatUKDate(project.created_at)}</span>
                </div>
              </div>

              {/* Payment Schedule */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h4 className="text-base font-semibold text-gray-900 mb-4 font-heading">Payment Instalments</h4>
                <div className="space-y-3">
                  {project.milestones.map((milestone, index) => {
                    const isSelected = selectedPaymentOption?.id === milestone.id;
                    const isPaid = milestone.status === 'paid';
                    const isOverdue = milestone.due_date && new Date(milestone.due_date) < new Date() && !isPaid;
                    
                    return (
                      <div 
                        key={milestone.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50 border-2 border-blue-200' : 
                          isPaid ? 'bg-green-50 border border-green-200' :
                          'border border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => !isPaid && setSelectedPaymentOption(milestone)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            isPaid ? 'bg-green-600' : 
                            isSelected ? 'bg-blue-600' :
                            isOverdue ? 'bg-red-600' : 'bg-gray-300'
                          }`} />
                          <div>
                            <p className="font-medium text-gray-900 font-body">
                              {getPaymentOptionLabel(milestone, index, project.payment_structure)}
                            </p>
                            <p className="text-sm text-gray-500 font-body">
                              {milestone.due_date ? `Due ${formatUKDate(milestone.due_date)}` : 'Due on agreement'}
                              {isPaid && <span className="text-green-600 ml-2">• Paid</span>}
                              {isOverdue && <span className="text-red-600 ml-2">• Overdue</span>}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 font-heading">
                            £{milestone.amount.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* VAT Breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between font-body">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">£{netAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-body">
                  <span className="text-gray-600">VAT ({project.vat_rate}%)</span>
                  <span className="text-gray-900">£{vatAmount.toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between text-lg font-bold">
                  <span className="font-heading">Total</span>
                  <span className="font-heading">£{totalWithVat.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Pane - Right Side / Bottom on Mobile */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 font-heading">Payment</h2>

              {/* Demo Payment Mode */}
              {projectData.demo ? (
                <div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800 font-body font-medium">Demo Mode</p>
                    <p className="text-xs text-blue-600 font-body mt-1">This simulates a real payment experience</p>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    {(() => {
                      const demoStructure = projectData?.project?.payment_structure;
                      const demoIsBnpl = demoStructure === 'bnpl';
                      if (demoIsBnpl) {
                        return (
                          <>
                            {/* Klarna — demo display */}
                            <div className="w-full p-4 border-2 border-pink-500 bg-pink-50 rounded-lg text-left" data-testid="demo-payment-method-klarna">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 rounded-lg bg-[#ffa8cd] flex items-center justify-center">
                                    <span className="font-black text-black text-sm">K.</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-900 font-body block">Klarna</span>
                                    <span className="text-xs text-gray-500 font-body">Pay in 3 interest-free instalments. Subject to approval.</span>
                                  </div>
                                </div>
                                <span className="text-xs font-semibold text-pink-700 bg-pink-100 px-2 py-1 rounded">BNPL</span>
                              </div>
                            </div>

                            {/* Clearpay — demo display */}
                            <div className="w-full p-4 border border-gray-200 rounded-lg text-left" data-testid="demo-payment-method-clearpay">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 rounded-lg bg-[#b2fce4] flex items-center justify-center">
                                    <span className="font-black text-black text-sm">◊</span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-900 font-body block">Clearpay</span>
                                    <span className="text-xs text-gray-500 font-body">Pay in 4 interest-free instalments. Subject to approval.</span>
                                  </div>
                                </div>
                                <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">BNPL</span>
                              </div>
                            </div>

                            {/* Simulated redirect notice */}
                            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-center">
                              <p className="text-sm text-gray-600 font-body">
                                In production, your lender's secure flow will open here.
                              </p>
                            </div>
                          </>
                        );
                      }
                      return (
                        <>
                    {/* Apple Pay */}
                    <div className="w-full p-4 border border-gray-200 rounded-lg text-left">
                      <div className="flex items-center space-x-3">
                        <img src="https://customer-assets.emergentagent.com/job_paidd-connect/artifacts/6189yk3q_apple-pay.png" alt="Apple Pay" className="h-8" />
                        <span className="font-medium text-gray-900 font-body">Apple Pay</span>
                      </div>
                    </div>

                    {/* Google Pay */}
                    <div className="w-full p-4 border border-gray-200 rounded-lg text-left">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl font-bold text-blue-600">G</span>
                        <span className="font-medium text-gray-900 font-body">Google Pay</span>
                      </div>
                    </div>

                    {/* Card - selected */}
                    <div className="w-full p-4 border-2 border-blue-500 bg-blue-50 rounded-lg text-left">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <CreditCard className="w-6 h-6 text-blue-600" />
                          <span className="font-medium text-gray-900 font-body">Debit or Credit Card</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" className="h-6" />
                          <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Simulated card input */}
                    <div className="p-4 border border-gray-200 rounded-lg bg-white">
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-600 font-body">Card Number</label>
                          <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md text-gray-500 text-sm font-mono">4242 4242 4242 4242</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm text-gray-600 font-body">Expiry</label>
                            <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md text-gray-500 text-sm font-mono">12/28</div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 font-body">CVC</label>
                            <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md text-gray-500 text-sm font-mono">123</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bank Debit */}
                    <div className="w-full p-4 border border-gray-200 rounded-lg text-left">
                      <div className="flex items-center space-x-3">
                        <Banknote className="w-6 h-6 text-gray-600" />
                        <div>
                          <span className="font-medium text-gray-900 font-body block">Bank Debit (via Stripe)</span>
                          <span className="text-xs text-gray-500 font-body">BACS for UK, SEPA for EU</span>
                        </div>
                      </div>
                    </div>
                        </>
                      );
                    })()}
                  </div>

                  <Button
                    onClick={async () => {
                      setProcessing(true);
                      try {
                        await new Promise(r => setTimeout(r, 2000));
                        await axios.post(`${API}/checkout/${slug}/demo-payment`);
                        setPaymentSuccess(true);
                        toast({ title: "Payment Successful", description: "Demo payment has been processed successfully." });
                      } catch (err) {
                        toast({ title: "Error", description: err.response?.data?.detail || "Payment failed", variant: "destructive" });
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing || !selectedPaymentOption || selectedPaymentOption.status === 'paid'}
                    className="w-full bg-[#e6007e] hover:bg-[#cc006f] text-white py-6 text-lg font-semibold rounded-lg font-body"
                  >
                    {processing ? (
                      <span className="flex items-center justify-center space-x-2">
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        <span>Processing Payment...</span>
                      </span>
                    ) : (
                      `Pay £${selectedPaymentOption ? selectedPaymentOption.amount.toLocaleString() : '0'} Now`
                    )}
                  </Button>

                  {/* Provider Badge */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-3 font-body">Powered by</p>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-1">
                        <div className="w-6 h-4 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">S</div>
                        <span className="text-xs text-gray-600 font-body">Stripe</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
              /* Real Payment Methods */
              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4 font-heading">Payment Methods</h3>
                {(() => {
                  // Restrict payment methods based on selected payment structure:
                  //  - credit_card: ONLY card (no Apple/Google/Bank)
                  //  - bnpl: ONLY BNPL (Klarna/Clearpay, redirect-based)
                  //  - fifty_fifty / three_month_milestones: card + wallets (no Bank Debit)
                  //  - full_upfront (default): all four
                  const structure = projectData?.project?.payment_structure;
                  const isBnpl = structure === 'bnpl';
                  const showWallets = structure !== 'credit_card' && !isBnpl;
                  const showCard = !isBnpl;
                  const showBankDebit = structure === 'full_upfront';
                  const showBnpl = isBnpl;
                  return (
                <div className="space-y-3">
                  {/* BNPL — Klarna and Clearpay as two separate options */}
                  {showBnpl && (
                  <>
                  <button
                    data-testid="payment-method-klarna"
                    onClick={() => handlePaymentMethodSelect('klarna')}
                    className={`w-full p-4 border rounded-lg text-left transition-colors ${
                      selectedPaymentMethod === 'klarna' ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg bg-[#ffa8cd] flex items-center justify-center">
                          <span className="font-black text-black text-sm">K.</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 font-body block">Klarna</span>
                          <span className="text-xs text-gray-500 font-body">Pay in 3 interest-free instalments. Subject to approval.</span>
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-pink-700 bg-pink-100 px-2 py-1 rounded">BNPL</span>
                    </div>
                  </button>

                  <button
                    data-testid="payment-method-clearpay"
                    onClick={() => handlePaymentMethodSelect('clearpay')}
                    className={`w-full p-4 border rounded-lg text-left transition-colors ${
                      selectedPaymentMethod === 'clearpay' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg bg-[#b2fce4] flex items-center justify-center">
                          <span className="font-black text-black text-sm">◊</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 font-body block">Clearpay</span>
                          <span className="text-xs text-gray-500 font-body">Pay in 4 interest-free instalments. Subject to approval.</span>
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">BNPL</span>
                    </div>
                  </button>
                  </>
                  )}

                  {/* Apple Pay */}
                  {showWallets && (
                  <button 
                    onClick={() => handlePaymentMethodSelect('apple_pay')}
                    className={`w-full p-4 border rounded-lg text-left transition-colors ${
                      selectedPaymentMethod === 'apple_pay' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_paidd-connect/artifacts/6189yk3q_apple-pay.png" 
                        alt="Apple Pay" 
                        className="h-8"
                      />
                      <span className="font-medium text-gray-900 font-body">Apple Pay</span>
                    </div>
                  </button>
                  )}

                  {/* Google Pay */}
                  {showWallets && (
                  <button 
                    onClick={() => handlePaymentMethodSelect('google_pay')}
                    className={`w-full p-4 border rounded-lg text-left transition-colors ${
                      selectedPaymentMethod === 'google_pay' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl font-bold text-blue-600">G</span>
                      <span className="font-medium text-gray-900 font-body">Google Pay</span>
                    </div>
                  </button>
                  )}

                  {/* Debit or Credit Card */}
                  {showCard && (
                  <button 
                    onClick={() => handlePaymentMethodSelect('card')}
                    className={`w-full p-4 border rounded-lg text-left transition-colors ${
                      selectedPaymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <CreditCard className="w-6 h-6 text-gray-600" />
                        <span className="font-medium text-gray-900 font-body">
                          {structure === 'credit_card' ? 'Credit Card Only' : 'Debit or Credit Card'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" className="h-6" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6" />
                      </div>
                    </div>
                  </button>
                  )}

                  {/* Bank Debit (via Stripe) — only on 100% Upfront */}
                  {showBankDebit && (
                  <button 
                    onClick={() => handlePaymentMethodSelect('bank_debit')}
                    className={`w-full p-4 border rounded-lg text-left transition-colors ${
                      selectedPaymentMethod === 'bank_debit' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Banknote className="w-6 h-6 text-gray-600" />
                      <div>
                        <span className="font-medium text-gray-900 font-body block">Bank Transfer / Pay by Bank</span>
                        <span className="text-xs text-gray-500 font-body">BACS Direct Debit / Open Banking · Available on 100% Upfront only</span>
                      </div>
                    </div>
                  </button>
                  )}
                  
                  {/* Payment Input - Shows when any method is selected */}
                  {showPaymentMethods && (
                    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-white">
                      <div className="mb-4">
                        {/* Apple Pay uses Payment Request Button */}
                        {selectedPaymentMethod === 'apple_pay' ? (
                          <div>
                            {canMakeApplePay ? (
                              <div id="payment-request-button" className="min-h-[48px]"></div>
                            ) : (
                              <div className="text-center py-6">
                                <div className="text-gray-500 mb-2">
                                  <img 
                                    src="https://customer-assets.emergentagent.com/job_paidd-connect/artifacts/6189yk3q_apple-pay.png" 
                                    alt="Apple Pay" 
                                    className="h-10 mx-auto mb-3 opacity-50"
                                  />
                                </div>
                                <p className="text-sm text-gray-600 font-body">
                                  Apple Pay is not available on this device.
                                </p>
                                <p className="text-xs text-gray-500 mt-1 font-body">
                                  Please use Safari on an Apple device with Apple Pay enabled, or choose another payment method.
                                </p>
                              </div>
                            )}
                          </div>
                        ) : selectedPaymentMethod === 'google_pay' ? (
                          /* Google Pay uses Payment Request Button */
                          <div>
                            {canMakeGooglePay ? (
                              <div id="payment-request-button" className="min-h-[48px]"></div>
                            ) : (
                              <div className="text-center py-6">
                                <div className="text-gray-500 mb-2">
                                  <svg className="h-10 mx-auto mb-3 opacity-50" viewBox="0 0 41 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19.526 2.635v4.083h2.518c.6 0 1.096-.202 1.488-.605.403-.402.605-.882.605-1.437 0-.544-.202-1.018-.605-1.422-.392-.413-.888-.62-1.488-.62h-2.518zm0 5.52v4.736h-1.504V1.198h3.99c1.013 0 1.873.337 2.582 1.012.72.675 1.08 1.497 1.08 2.466 0 .991-.36 1.819-1.08 2.482-.697.665-1.559.997-2.583.997h-2.485z" fill="#5F6368"/>
                                    <path d="M27.194 10.442c0 .544.166.96.499 1.247.333.286.73.429 1.192.429.544 0 1.013-.201 1.405-.604.393-.403.59-.908.59-1.516 0-.327-.101-.618-.304-.87-.203-.253-.463-.45-.782-.59-.318-.142-.637-.213-.958-.213-.544 0-1.013.152-1.405.455-.393.303-.237.952-.237 1.662zm3.686-3.666v.58h-.032c-.286-.338-.618-.584-.997-.738-.378-.154-.782-.231-1.21-.231-.86 0-1.59.303-2.19.908-.6.606-.9 1.373-.9 2.303 0 .93.3 1.697.9 2.303.6.605 1.33.908 2.19.908.428 0 .832-.077 1.21-.23.379-.155.71-.401.997-.739h.032v.546c0 1.085-.531 1.628-1.593 1.628-.597 0-1.087-.235-1.471-.705l-1.044.87c.598.714 1.421 1.07 2.467 1.07 1.855 0 2.782-.95 2.782-2.85V6.776h-1.141z" fill="#5F6368"/>
                                    <path d="M32.9 1.905l1.504.87 5.15 8.926-1.504.87-5.15-8.926zm-1.504 8.926l1.504-.87 5.15 8.926-1.504.87-5.15-8.926z" fill="#4285F4"/>
                                  </svg>
                                </div>
                                <p className="text-sm text-gray-600 font-body">
                                  Google Pay is not available on this device.
                                </p>
                                <p className="text-xs text-gray-500 mt-1 font-body">
                                  Please use Chrome with Google Pay enabled, or choose another payment method.
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Card and Bank Debit use Payment Element */
                          <div id="payment-element" className="min-h-[200px]"></div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <Shield className="w-4 h-4" />
                        <span>Secured by Stripe - All payment details are encrypted</span>
                      </div>
                    </div>
                  )}

                </div>

                );
                })()}
                {/* Provider Badge */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-3 font-body">Powered by</p>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      <div className="w-6 h-4 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">S</div>
                      <span className="text-xs text-gray-600 font-body">Stripe</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Contract Section */}
              <div className="mb-6 p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 font-heading">Service Agreement</h4>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      // Ensure contract content is loaded
                      if (!contract?.content && projectData?.slug) {
                        try {
                          const response = await axios.get(`${API}/checkout/${projectData.slug}`, {
                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                          });
                          // Update the contract content in the parent component if possible
                          console.log('Loaded contract content for modal');
                        } catch (error) {
                          console.error('Failed to load contract:', error);
                        }
                      }
                      setShowFullContract(true);
                    }}
                    className="text-xs font-body"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View Full
                  </Button>
                </div>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ <strong>Important:</strong> Click "View Full" above to read the complete service agreement before signing.
                  </p>
                </div>
                
                {!contractSigned && (
                  <div className="space-y-4">
                    {/* UK Government Compliant Signature Interface */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-blue-900 font-body">UK Government Compliant Electronic Signature</span>
                      </div>
                      <p className="text-xs text-blue-700 font-body">
                        Secure electronic signature with email verification, audit trail, and legal compliance for UK government contracts.
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="signature_name" className="text-sm font-body">Full Legal Name *</Label>
                      <Input
                        id="signature_name"
                        value={signatureData.full_name}
                        onChange={(e) => {
                          setSignatureData({ ...signatureData, full_name: e.target.value });
                        }}
                        placeholder="Enter your full legal name as it appears on official documents"
                        className="font-body text-sm"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1 font-body">
                        This name will be used for legal verification and must match official records.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="signature_email" className="text-sm font-body">Email Address (For Verification) *</Label>
                      <Input
                        id="signature_email"
                        type="email"
                        value={signatureData.email || projectData?.project?.client_email || ''}
                        onChange={(e) => {
                          setSignatureData({ ...signatureData, email: e.target.value });
                        }}
                        placeholder="Enter your email address for signature verification"
                        className="font-body text-sm"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1 font-body">
                        A verification email will be sent to confirm your signature.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="signature_position" className="text-sm font-body">Position/Title *</Label>
                      <Input
                        id="signature_position"
                        value={signatureData.position || ''}
                        onChange={(e) => {
                          setSignatureData({ ...signatureData, position: e.target.value });
                        }}
                        placeholder="e.g., Director, Managing Partner, Authorized Representative"
                        className="font-body text-sm"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1 font-body">
                        Required to establish signing authority for government contracts.
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="signature_company" className="text-sm font-body">Company Name (Optional)</Label>
                      <Input
                        id="signature_company"
                        value={signatureData.company || ''}
                        onChange={(e) => {
                          setSignatureData({ ...signatureData, company: e.target.value });
                        }}
                        placeholder="e.g., ABC Ltd, Smith & Associates"
                        className="font-body text-sm"
                      />
                    </div>
                    
                    <div className="space-y-3 border-t pt-4">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="identity_confirmed"
                          checked={signatureData.identity_confirmed || false}
                          onCheckedChange={(checked) => setSignatureData({ ...signatureData, identity_confirmed: checked })}
                        />
                        <Label htmlFor="identity_confirmed" className="text-xs leading-4 font-body">
                          I confirm that I am the person named above and have the authority to sign this agreement on behalf of the organization (if applicable)
                        </Label>
                      </div>
                      
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="contract_agreed"
                          checked={signatureData.agreed}
                          onCheckedChange={(checked) => setSignatureData({ ...signatureData, agreed: checked })}
                        />
                        <Label htmlFor="contract_agreed" className="text-xs leading-4 font-body">
                          I agree to the terms and conditions of this Service Agreement and authorize electronic signature processing in accordance with UK electronic signature regulations
                        </Label>
                      </div>
                    </div>
                      
                    <Button
                      onClick={handleEnhancedSignContract}
                      disabled={processing || !signatureData.agreed || !signatureData.identity_confirmed || !signatureData.full_name?.trim() || !signatureData.email?.trim() || !signatureData.position?.trim()}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-body text-sm"
                    >
                      {processing ? "Processing Signature..." : "Sign Agreement & Send Email Verification"}
                    </Button>
                    
                    <div className="text-xs text-gray-500 font-body border-t pt-3">
                      <p className="mb-1"><strong>What happens next:</strong></p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Your signature will be recorded with full audit trail</li>
                        <li>Email verification will be sent to the address provided</li>
                        <li>Click the verification link to complete the signing process</li>
                        <li>Contract becomes legally binding upon email verification</li>
                      </ol>
                    </div>
                  </div>
                )}

                {(contractSigned || verificationSent) && (
                  <div className="space-y-2">
                    {projectData?.project?.signature_pending && (
                      <div className="flex items-center space-x-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                        <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5 9.293 8.207a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L11 9.586l-.293-.293z" clipRule="evenodd" />
                        </svg>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium font-body">Signature Verification Pending</span>
                          <span className="text-xs text-amber-700 font-body">
                            Please check your email ({projectData.project.contract_signer_email}) and click the verification link
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {contractSigned && !projectData?.project?.signature_pending && (
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <div className="flex flex-col">
                          <span className="text-sm font-body">Contract signed & verified ✓</span>
                          {projectData?.project?.signature_audit_trail?.signature_method && (
                            <div className="flex items-center space-x-1 mt-1">
                              <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="text-xs text-blue-600 font-body">
                                UK Government Compliant Electronic Signature
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pay Button */}
              <div className="space-y-4">
                <Button
                  onClick={handlePayment}
                  disabled={!contractSigned || !selectedPaymentMethod || !selectedPaymentOption || processing}
                  className="w-full h-12 bg-[#e6007e] hover:bg-[#c4006b] text-white font-semibold font-body"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Pay securely £{selectedPaymentOption?.amount.toLocaleString() || '0'}
                    </>
                  )}
                </Button>

                {/* Pay All Remaining Option - Only show if multiple unpaid instalments */}
                {contractSigned && projectData?.project?.milestones?.filter(m => m.status !== 'paid').length > 1 && (
                  <Button
                    onClick={handlePayAllRemaining}
                    disabled={!contractSigned || !selectedPaymentMethod || processing}
                    variant="outline"
                    className="w-full h-12 border-[#e6007e] text-[#e6007e] hover:bg-[#e6007e] hover:text-white font-semibold font-body"
                  >
                    {processing ? "Processing..." : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Pay all remaining instalments early
                        <span className="ml-2 text-sm">
                          (£{projectData?.project?.milestones?.filter(m => m.status !== 'paid').reduce((sum, m) => sum + m.amount, 0).toLocaleString()})
                        </span>
                      </>
                    )}
                  </Button>
                )}

                {/* Trust Badge */}
                <div className="flex items-center justify-center space-x-2 text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span className="text-xs font-body">256-bit SSL encryption • PCI DSS compliant • Secure checkout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Full Contract Modal */}
      {showFullContract && (
        <Dialog open={showFullContract} onOpenChange={setShowFullContract}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Service Agreement - {project.title}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div className="bg-white rounded-lg border shadow-sm">
                <div 
                  dangerouslySetInnerHTML={{ __html: contract?.content || project?.contract?.content || '' }}
                  className="contract-preview"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Toaster />
    </div>
  );
};

// Quote Client View Component - Clean implementation

export default CheckoutPage;
